import { tmxApiCall } from './index'

// Utilities
import Logger from '../utility/loggerUtil'

export default {
  /**
   * Gets the customer info (name, phone, address, etc.) based on the LMS.
   * @param {String} lmsId - The active LMS ID based on the most recently active loan.
   * @param {String} [loanHeader] - The loan header ID that the cosigner is associated with (if getting info for cosigner).
   * @param {String} [storeId] - The store ID that the cosigner is associated with (if getting info for cosigner).
   */
  async getCustomerInfo (lmsId, loanHeader = undefined, storeId = undefined) {
    Logger.info('Started get personal info query.')

    return tmxApiCall('get', '/personalInfo', {
      params: {
        lmsId,
        loanHeader,
        storeId
      }
    })
  },

  /**
   * Save a new email address to a Vergent customer, will replace old if there is one (Only one email address can be saved).
   * @param {String} customerId - The identifier for a Vergent customer.
   * @param {String} email - The email address to save on the Vergent customer record.
   * @returns {Promise<Object>} Return data object.
   */
  async saveCustomerEmail (customerId, email) {
    Logger.info('Saving customer email')
    return tmxApiCall('post', '/customerinfo', {
      customer: {
        customerId,
        emails: [{ email }]
      }
    })
  },

  /**
   * Save communication settings for a customer
   * @param {String} customerId - The identifier for a Vergent customer.
   * @param {Object} communicationSettings - All of a customers communication settings.
   * @param {String} [loanHeader] - The header identifier for a loan.
   * @param {String} [storeId] - The identifier for a Store.
   * @returns {Promise<Object>} Customer communication settings information.
   */
  async updateCommunicationSettings (customerId, communicationSettings, loanHeader, storeId) {
    Logger.info('Saving communication settings')
    return tmxApiCall('post', '/customerinfo', {
      customer: {
        customerId,
        communicationPreferences: communicationSettings
      },
      loanHeader,
      storeId
    })
  },

  async saveCustomerInfo (customerId, phoneNumber, addresses, cognitoUserId, lastPayDate, payFrequency, email, ssn) {
    Logger.info('Started save customer info query.')
    return tmxApiCall('post', '/customerinfo', {
      customer: {
        customerId,
        contactNumbers: phoneNumber ? [phoneNumber] : undefined,
        emails: email ? [email] : undefined,
        addresses,
        cognitoUserId,
        lastPayDate,
        payFrequency,
        ssn
      }
    })
  },

  /**
   * Saves demographic info to customer profile.
   * @param {Object} demographics - Object of a customer's demographics to be saved.
   * @returns {Promise<Object>} Customer demographics information.
   */
  async updateCustomerDemographics (demographics) {
    Logger.info('Saving customer demographics.')
    return tmxApiCall('post', '/customerinfo', {
      customer: {
        demographics
      }
    })
  },

  async sendAuthorizationNotification (customerId) {
    Logger.info('Started sending customer authorization notification message.')
    return tmxApiCall('post', '/replaceCustomer', {
      customerId: customerId.toString()
    })
  },

  /**
   * Updates a loans association with a different customer
   * @param {String} username - The username on the previous associated profile.
   * @param {String} password - The password on the previous associated profile.
   * @param {String} customerId - The identifier for the previously associated Vergent customer.
   * @returns {Promise<Object>} Return data object.
   */
  async replaceCustomerWithPreviousCredentials (username, password, customerId) {
    Logger.info('Replacing customer on loan with previous credentials')
    return tmxApiCall('put', '/replaceCustomer', {
      username,
      password,
      customerId
    })
  },

  async replaceCustomer (returnId, profileId) {
    Logger.info('Started confirming replace customer call.')
    return tmxApiCall('put', '/replaceCustomer', {
      returnId,
      profileId
    })
  },

  async checkReplaceAccountStatus (customerId) {
    Logger.info('Started checking account replacement status.')
    return tmxApiCall('get', '/replaceCustomer', {
      params: {
        customerId: customerId.toString()
      }
    })
  },

  async getServiceProviders (includeBrands, includeAvailability) {
    Logger.info('Started checking service providers.')
    return tmxApiCall('get', '/serviceProvider', {
      params: {
        brand: includeBrands,
        availability: includeAvailability
      }
    })
  },

  /**
   * Checks if a customer is in bankruptcy status
   * @param {String} customerId - The identifier for a customer.
   * @param {String} loanHeader - The header identifier for a loan.
   * @param {String|Number} storeId - The identifier for a store.
   * @returns {Promise<{isInBankruptcy: Boolean}>} Returns boolean for if customer is in bankruptcy status.
   */
  async getBankruptcyStatus (customerId, loanHeader, storeId) {
    Logger.info('Checking Bankruptcy Status')
    return tmxApiCall('get', '/bankruptcyStatus', {
      params: {
        customerId,
        loanHeader,
        storeId
      }
    })
  },

  /**
   * Verifies a customers social security number.
   * @param {String} customerId - The identifier for a customer.
   * @param {String} loanHeader - The header identifier for a loan.
   * @param {String} loanId - The identifier for a loan.
   * @param {String} productId - The identifier for the type of loan product.
   * @param {String} storeId - The store identifier.
   */
  async experianSSNCheck (customerId, loanHeader, loanId, productId, storeId) {
    Logger.info('Checking Experian SSN')
    return tmxApiCall('get', '/experianStatus', {
      params: {
        customerId,
        loanHeader,
        loanId,
        productId,
        storeId
      }
    })
  },

  async updateBankingInfo (customerId, bankName, routingNumber, accountNumber) {
    Logger.info('Started save customer info query.')
    return tmxApiCall('post', '/customerinfo', {
      customer: {
        customerId,
        bankName,
        routingNumber,
        accountNumber
      }
    })
  },

  async getBankInfo (routingNumber) {
    return tmxApiCall('get', '/bankInfo', {
      params: {
        routingNumber
      }
    }).catch(error => {
      Logger.error('Failed to fetch bank name in getBankInfo')
      Logger.debug(error)
      throw error
    })
  },

  async guestLookup (dateOfBirth, ssn, referralId) {
    return tmxApiCall('get', '/guestLookup', {
      params: {
        dateOfBirth,
        ssn,
        referralId
      }
    }).catch(error => {
      Logger.error('Failed to fetch guestLookup')
      Logger.debug(error)
      throw error
    })
  }
}
